import { api } from '@converse/headless';
import { __ } from 'i18n';
import BaseModal from 'plugins/modal/modal.js';
import tplCreateNewsModal from '../templates/create-news-modal.js';
import NoorConfig from '../../../shared/noor-config.js';

class CreateNewsModal extends BaseModal {
    constructor(options) {
        super(options);
        this.selectedSubjects = [];
        this.subjects = []; // Initialize with empty array
        
        // Bind event handler
        this.handleProfileUpdate = this.handleProfileUpdate.bind(this);
    }

    async initialize() {
        super.initialize();
        // Load subjects from stored profile data
        this.subjects = await this.loadSubjects();
        // Trigger re-render after subjects are loaded
        this.requestUpdate();

        // Listen for profile updates
        window.addEventListener('noor-profile-updated', this.handleProfileUpdate);
    }

    /**
     * Handle profile update event
     */
    async handleProfileUpdate(event) {
        console.log('🔄 Create News: Profile updated, reloading subjects...', event.detail);
        this.subjects = await this.loadSubjects();
        this.requestUpdate();
        console.log('✅ Create News: Subjects reloaded');
    }

    /**
     * Cleanup event listeners
     */
    disconnectedCallback() {
        super.disconnectedCallback();
        window.removeEventListener('noor-profile-updated', this.handleProfileUpdate);
    }

    /**
     * Load subjects from IndexedDB profile data
     */
    async loadSubjects() {
        try {
            const db = await this.openIndexedDB();
            const profile = await this.getProfileData(db);
            console.log('📊 Create News: Loaded profile from IndexedDB:', profile);
            if (profile && profile.subjects && profile.subjects.length > 0) {
                console.log('✅ Using subjects from profile:', profile.subjects.length, 'subjects');
                return profile.subjects;
            } else {
                console.warn('⚠️ No subjects in profile, using fallback');
            }
        } catch (error) {
            console.warn('Failed to load subjects from profile:', error);
        }
        // Fallback to default subjects
        console.log('📋 Using fallback subjects');
        return ["سیاسی", "امنیتی", "اقتصادی", "سایبری", "فرهنگی"];
    }

    /**
     * Get profile data from IndexedDB
     * @param {IDBDatabase} db
     */
    getProfileData(db) {
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(['profile'], 'readonly');
            const store = transaction.objectStore('profile');
            const request = store.get(1);
            request.onsuccess = () => resolve(request.result || {});
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Convert Persian/Arabic digits to English
     * @param {string} str
     */
    convertPersianDigitsToEnglish(str) {
        const persianDigits = '۰۱۲۳۴۵۶۷۸۹';
        const arabicDigits = '٠١٢٣٤٥٦٧٨٩';
        return str.replace(/[۰-۹٠-٩]/g, (c) => {
            const persianIndex = persianDigits.indexOf(c);
            if (persianIndex !== -1) return persianIndex.toString();
            const arabicIndex = arabicDigits.indexOf(c);
            if (arabicIndex !== -1) return arabicIndex.toString();
            return c;
        });
    }

    /**
     * Format date for display: 1404/12/11-15:34
     * @param {string} dateInt - Date as YYYYMMDDHHMMSS
     */
    formatDateForDisplay(dateInt) {
        if (dateInt.length !== 14) return dateInt;
        const year = dateInt.substring(0, 4);
        const month = dateInt.substring(4, 6);
        const day = dateInt.substring(6, 8);
        const hour = dateInt.substring(8, 10);
        const minute = dateInt.substring(10, 12);
        return `${year}/${month}/${day}-${hour}:${minute}`;
    }

    getModalTitle() {
        return 'ایجاد خبر';
    }

    /**
     * @param {MouseEvent} ev
     */
    toggleSubject(ev) {
        const checkbox = /** @type {HTMLInputElement} */ (ev.target);
        const value = checkbox.value;
        
        if (checkbox.checked) {
            if (!this.selectedSubjects.includes(value)) {
                this.selectedSubjects.push(value);
            }
        } else {
            this.selectedSubjects = this.selectedSubjects.filter(s => s !== value);
        }
    }

    /**
     * @param {Event} ev
     */
    async submitForm(ev) {
        ev.preventDefault();
        const form = /** @type {HTMLFormElement} */ (ev.target);
        const formData = new FormData(form);

        try {
            // Get username from IndexedDB (from JWT auth)
            const db = await this.openIndexedDB();
            const authData = await this.getAuthData(db);
            const username = authData.username || localStorage.getItem('username') || '';

            // Get and convert date from Persian digits to English
            const dateRaw = formData.get('date');
            const dateEnglish = this.convertPersianDigitsToEnglish(dateRaw);
            
            // Get lat and lon values
            const lat = formData.get('lat');
            const lon = formData.get('lon');
            
            // Build the data object
            const newsData = {
                date: dateEnglish,
                desc: formData.get('desc'),
                subjects: this.selectedSubjects,
                lat: lat || '',
                lon: lon || '',
                title: formData.get('title'),
                username: username,
                key: username
            };

            // Get the AES key from user input (or use username as default)
            const aesKey = username;

            // Ensure crypto.js data_encryptor is available globally
            if (typeof data_encryptor !== 'function') {
                throw new Error('Crypto library not loaded. Please ensure crypto.js is included in index.html.');
            }

            // Encrypt the data
            const encrypted = await data_encryptor(newsData, aesKey);

            // Prepare form data for API
            const apiFormData = new FormData();
            apiFormData.append('data', encrypted.data);
            apiFormData.append('key', encrypted.key);

            // Add file if present
            const fileInput = /** @type {HTMLInputElement} */ (form.querySelector('input[type="file"]'));
            if (fileInput && fileInput.files && fileInput.files.length > 0) {
                apiFormData.append('files', fileInput.files[0]);
            }

            // Get JWT token from IndexedDB for authorization (reuse db from above)
            const token = await this.getAccessToken(db);

            if (!token) {
                throw new Error('No JWT token found. Please login first.');
            }

            console.log('Sending news creation request with token:', token.substring(0, 20) + '...');
            console.log('Encrypted data length:', encrypted.data.length);
            console.log('Encrypted key length:', encrypted.key.length);

            // Send to API
            const apiUrl = `${NoorConfig.api.baseUrl}${NoorConfig.api.endpoints.newsCreate}`;
            console.log('Sending news to:', apiUrl);
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: apiFormData
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('API Error:', response.status, errorText);
                throw new Error(`API error: ${response.status} ${response.statusText}`);
            }

            const result = await response.json();
            console.log('News created successfully:', result);

            // Show success message
            api.alert('success', __('Success'), __('News created successfully!'));

            // Close the modal
            this.modal.hide();

            // Reset form
            form.reset();
            this.selectedSubjects = [];

        } catch (error) {
            console.error('Error creating news:', error);
            api.alert('error', __('Error'), __('Failed to create news: ') + error.message);
        }
    }

    /**
     * Open IndexedDB to get JWT token
     */
    openIndexedDB() {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open('NoorDB', 2); // Increment version
            
            request.onupgradeneeded = (event) => {
                const db = event.target.result;
                // Create auth store if it doesn't exist
                if (!db.objectStoreNames.contains('auth')) {
                    db.createObjectStore('auth', { keyPath: 'id' });
                }
                // Create profile store if it doesn't exist
                if (!db.objectStoreNames.contains('profile')) {
                    db.createObjectStore('profile', { keyPath: 'id' });
                }
            };
            
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Get auth data from IndexedDB
     * @param {IDBDatabase} db
     */
    getAuthData(db) {
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(['auth'], 'readonly');
            const store = transaction.objectStore('auth');
            const request = store.get(1);
            request.onsuccess = () => {
                const authData = request.result || {};
                resolve(authData);
            };
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Get access token from IndexedDB
     * @param {IDBDatabase} db
     */
    getAccessToken(db) {
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(['auth'], 'readonly');
            const store = transaction.objectStore('auth');
            const request = store.get(1); // Get the record with id: 1
            request.onsuccess = () => {
                const authData = request.result;
                if (authData && authData.accessToken) {
                    console.log('JWT token retrieved from IndexedDB');
                    resolve(authData.accessToken);
                } else {
                    console.warn('No JWT token found in IndexedDB');
                    resolve('');
                }
            };
            request.onerror = () => reject(request.error);
        });
    }

    renderModal() {
        return tplCreateNewsModal(this);
    }

    /**
     * Initialize Persian date picker
     * @param {FocusEvent} ev
     */
    initPersianDatePicker(ev) {
        const input = /** @type {HTMLInputElement} */ (ev.target);
        const self = this;
        
        // Check if Persian Date Picker library is loaded (jQuery-based)
        if (typeof window.jQuery !== 'undefined' && typeof window.jQuery.fn.persianDatepicker === 'function') {
            window.jQuery(input).persianDatepicker({
                format: 'YYYY/MM/DD HH:mm',
                initialValue: false,
                autoClose: true,
                timePicker: {
                    enabled: true
                },
                onSelect: function(unixTime) {
                    // Get the formatted date from the picker (in Persian digits)
                    let dateStr = window.jQuery(input).val();
                    // Convert Persian/Arabic digits to English
                    dateStr = self.convertPersianDigitsToEnglish(dateStr);
                    // Convert to format: YYYYMMDDHHMMSS (remove slashes, spaces, colons) + add 00 for seconds
                    const formatted = dateStr.replace(/[/\s:]/g, '') + '00';
                    // Set the hidden input with the integer format
                    const hiddenInput = document.getElementById('news-date');
                    if (hiddenInput) {
                        hiddenInput.value = formatted;
                    }
                    // Display in readable format: 1404/12/11-15:34
                    input.value = self.formatDateForDisplay(formatted);
                }
            });
        } else {
            console.warn('Persian Date Picker not loaded. Using plain text input.');
            console.log('Please ensure jQuery and persian-datepicker are included in index.html');
        }
    }

    /**
     * Initialize OpenStreetMap location picker
     */
    initMapPicker() {
        // Check if Leaflet is loaded
        if (typeof window.L !== 'undefined') {
            const mapContainer = document.getElementById('news-map');
            if (!mapContainer || mapContainer._leaflet_id) return; // Already initialized

            // Create map centered on Iran
            const map = window.L.map('news-map').setView([32.4279, 53.6880], 6);

            // Add custom map tiles from map.misaq.me
            window.L.tileLayer('https://map.misaq.me/osm_tiles/{z}/{x}/{y}.png', {
                attribution: '© OpenStreetMap contributors',
                maxZoom: 18
            }).addTo(map);

            let marker = null;

            // Click to set location
            map.on('click', (e) => {
                const latInput = document.getElementById('news-lat');
                const lonInput = document.getElementById('news-lon');
                
                if (latInput && lonInput) {
                    latInput.value = e.latlng.lat.toFixed(8);
                    lonInput.value = e.latlng.lng.toFixed(8);

                    // Remove old marker
                    if (marker) {
                        map.removeLayer(marker);
                    }

                    // Add new marker
                    marker = window.L.marker([e.latlng.lat, e.latlng.lng]).addTo(map);
                }
            });

            // Set initial marker if values exist
            setTimeout(() => {
                const latInput = document.getElementById('news-lat');
                const lonInput = document.getElementById('news-lon');
                if (latInput && lonInput && latInput.value && lonInput.value) {
                    const lat = parseFloat(latInput.value);
                    const lon = parseFloat(lonInput.value);
                    if (!isNaN(lat) && !isNaN(lon)) {
                        marker = window.L.marker([lat, lon]).addTo(map);
                        map.setView([lat, lon], 10);
                    }
                }
            }, 100);
        }
    }
}

api.elements.define('converse-create-news-modal', CreateNewsModal);

export default CreateNewsModal;
